import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { formatWhen } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { TagChips } from "../email/RuleEditor";
import { useCustomFields, useTags } from "../contacts/hooks";

export interface FormSettings {
  apply_tag_ids?: number[];
  remove_tag_ids?: number[];
  reapply_tag_ids?: number[];
  marketing_consent?: boolean;
  consent_field?: string;
  consent_tag_ids?: number[];
  consent_reason?: string;
  note?: { title?: string; body: string } | null;
  start_automation_ids?: number[];
  notify?: string[];
  notify_subject?: string;
  lead_source?: string;
  spam?: "auto" | "strict" | "off" | "";
  field_map?: Record<string, string>;
  overwrite?: string[];
  tags_by_field?: Record<string, Record<string, number[]>>;
  event_id?: number;
  session_id?: number;
  session_field?: string;
  hub_tag_ids?: number[];
  [k: string]: unknown; // field_map, tags_by_field, event settings…: kept as they are
}

export interface FormRow {
  id: number;
  key: string;
  name: string;
  description: string | null;
  active: boolean;
  settings: FormSettings;
}

const EDITED = new Set([
  "apply_tag_ids",
  "remove_tag_ids",
  "reapply_tag_ids",
  "marketing_consent",
  "consent_field",
  "consent_tag_ids",
  "consent_reason",
  "note",
  "start_automation_ids",
  "notify",
  "notify_subject",
  "lead_source",
  "spam",
  "field_map",
  "overwrite",
  "tags_by_field",
  "event_id",
  "session_id",
  "session_field",
  "hub_tag_ids",
]);

/** Contact fields a form may fill (internal/forms/settings.go mappableFields); custom fields go as custom.<key>. */
const CONTACT_FIELDS: [string, string][] = [
  ["first_name", "First name"],
  ["last_name", "Last name"],
  ["nickname", "Nickname"],
  ["prefix", "Prefix"],
  ["middle_name", "Middle name"],
  ["suffix", "Suffix"],
  ["company", "Company"],
  ["job_title", "Job title"],
  ["website", "Website"],
  ["time_zone", "Time zone"],
  ["language", "Language"],
  ["birthday", "Birthday"],
];

interface MapRow {
  from: string;
  to: string;
  overwrite: boolean;
}
interface AnswerRow {
  field: string;
  value: string;
  ids: number[];
}
type SessionMode = "next" | "fixed" | "field";

/** The everyday settings of one site form; anything else (field mapping, tags by answer, event sign-up) is kept as is. */
export function FormSettingsDialog({ form, onClose }: { form: FormRow; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(form.name);
  const [active, setActive] = useState(form.active);
  const [s, setS] = useState<FormSettings>({ ...form.settings });
  const set = (patch: Partial<FormSettings>) => setS((x) => ({ ...x, ...patch }));
  const [mapRows, setMapRows] = useState<MapRow[]>(() =>
    Object.entries(form.settings.field_map ?? {}).map(([from, to]) => ({ from, to, overwrite: (form.settings.overwrite ?? []).includes(to) })),
  );
  const [answerRows, setAnswerRows] = useState<AnswerRow[]>(() =>
    Object.entries(form.settings.tags_by_field ?? {}).flatMap(([field, byValue]) => Object.entries(byValue).map(([value, ids]) => ({ field, value, ids }))),
  );
  const [eventId, setEventId] = useState<number | "">(form.settings.event_id ?? "");
  const [sessionMode, setSessionMode] = useState<SessionMode>(form.settings.session_field ? "field" : form.settings.session_id ? "fixed" : "next");
  const [sessionId, setSessionId] = useState<number | "">(form.settings.session_id ?? "");
  const [sessionField, setSessionField] = useState(form.settings.session_field ?? "");
  const customFields = useCustomFields();
  const events = useQuery({
    queryKey: ["events", "all", "for-forms"],
    queryFn: () => api<{ events: { id: number; name: string; status: string; brand_id: number }[] }>("/api/v1/events" + qs({ when: "all", limit: 500 })),
  });
  const eventDetail = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => api<{ sessions: { id: number; name: string | null; starts_at: string; status: string }[] }>(`/api/v1/events/${eventId}`),
    enabled: eventId !== "",
  });
  const targets = useMemo(
    () => [
      ...CONTACT_FIELDS,
      ...(customFields.data?.fields ?? []).filter((f) => !f.archived_at).map((f) => [`custom.${f.key}`, `${f.label} (custom field)`] as [string, string]),
    ],
    [customFields.data],
  );
  const mapProblem = mapRows.some((r) => r.from.trim() && !r.to) || new Set(mapRows.filter((r) => r.from.trim()).map((r) => r.from.trim())).size !== mapRows.filter((r) => r.from.trim()).length;
  const answerProblem = answerRows.some((r) => (r.field.trim() || r.value.trim()) && (!r.field.trim() || !r.value.trim() || r.ids.length === 0));
  const autos = useQuery({
    queryKey: ["automations-list", "for-forms"],
    queryFn: () => api<{ automations: { id: number; name: string; status: string }[] }>("/api/v1/automations" + qs({ limit: 500 })),
  });
  const kept = Object.keys(s).filter((k) => !EDITED.has(k) && s[k] !== undefined && s[k] !== null && s[k] !== "");
  const save = useMutation({
    mutationFn: () => {
      const fieldMap = Object.fromEntries(mapRows.filter((r) => r.from.trim() && r.to).map((r) => [r.from.trim(), r.to]));
      const tagsByField: Record<string, Record<string, number[]>> = {};
      for (const r of answerRows.filter((r) => r.field.trim() && r.value.trim() && r.ids.length)) {
        (tagsByField[r.field.trim()] ??= {})[r.value.trim()] = r.ids;
      }
      const full: FormSettings = {
        ...s,
        field_map: Object.keys(fieldMap).length ? fieldMap : undefined,
        overwrite: mapRows.filter((r) => r.from.trim() && r.to && r.overwrite).map((r) => r.to),
        tags_by_field: Object.keys(tagsByField).length ? tagsByField : undefined,
        event_id: eventId || undefined,
        session_id: eventId && sessionMode === "fixed" && sessionId ? sessionId : undefined,
        session_field: eventId && sessionMode === "field" && sessionField.trim() ? sessionField.trim() : undefined,
      };
      const clean: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(full)) {
        if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
        clean[k] = v;
      }
      return api(`/api/v1/forms/${form.id}`, { method: "PATCH", json: { name, active, settings: clean } });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["forms"] });
      toast(`Saved ${name}`);
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  const note = s.note ?? { title: "", body: "" };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] w-[min(680px,calc(100vw-2rem))] overflow-y-auto">
        <DialogTitle>{form.name}</DialogTitle>
        <DialogDescription>
          What happens when someone sends <span className="num">{form.key}</span>. Changes apply to the next submission; people already processed aren't touched.
        </DialogDescription>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-[1fr_auto] items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="fs-name">Name</Label>
              <Input id="fs-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <label className="flex items-center gap-2 pb-2">
              <Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} /> On
            </label>
          </div>
          {!active && <p className="text-xs text-[var(--color-warning-fg)]">Off: submissions still create or match the person, but none of these settings run.</p>}

          <fieldset className="space-y-2 rounded-md border p-3">
            <legend className="px-1 text-xs font-semibold">Tags</legend>
            <Row label="Add every time">
              <TagChips ids={s.apply_tag_ids ?? []} onChange={(v) => set({ apply_tag_ids: v })} />
            </Row>
            <Row label="Remove every time">
              <TagChips ids={s.remove_tag_ids ?? []} onChange={(v) => set({ remove_tag_ids: v })} />
            </Row>
            <Row label="Remove, then add again" help="So someone who signs up again starts that tag's automation again (newsletter sign-ups).">
              <TagChips ids={s.reapply_tag_ids ?? []} onChange={(v) => set({ reapply_tag_ids: v })} />
            </Row>
          </fieldset>

          <fieldset className="space-y-2 rounded-md border p-3">
            <legend className="px-1 text-xs font-semibold">Email consent</legend>
            <label className="flex items-center gap-2">
              <Checkbox checked={!!s.marketing_consent} onChange={(e) => set({ marketing_consent: e.target.checked || undefined })} /> The whole form is a newsletter sign-up
            </label>
            {!s.marketing_consent && (
              <Row label="Opt-in checkbox field" help="The posted field that means they ticked “send me email” on a contact form.">
                <Input className="h-8" aria-label="Opt-in checkbox field" value={s.consent_field ?? ""} onChange={(e) => set({ consent_field: e.target.value || undefined })} placeholder="newsletter_opt_in" />
              </Row>
            )}
            <Row label="Tags when they opt in">
              <TagChips ids={s.consent_tag_ids ?? []} onChange={(v) => set({ consent_tag_ids: v })} />
            </Row>
            <Row label="Consent reason" help="Recorded with their subscription.">
              <Input className="h-8" aria-label="Consent reason" value={s.consent_reason ?? ""} onChange={(e) => set({ consent_reason: e.target.value || undefined })} placeholder="Signed up for the newsletter on MobileHomeUniversity.com" />
            </Row>
          </fieldset>

          <fieldset className="space-y-2 rounded-md border p-3">
            <legend className="px-1 text-xs font-semibold">Note, automations, team alert</legend>
            <Row label="Note title" help="Leave the note empty for none. ~Field.message~ inserts what they typed.">
              <Input className="h-8" aria-label="Note title" value={note.title ?? ""} onChange={(e) => set({ note: { ...note, title: e.target.value } })} placeholder="~Field.subject~" />
            </Row>
            <Row label="Note text">
              <Textarea className="min-h-16" aria-label="Note text" value={note.body ?? ""} onChange={(e) => set({ note: e.target.value || note.title ? { ...note, body: e.target.value } : null })} placeholder="~Field.message~" />
            </Row>
            <Row label="Start automations">
              <div className="space-y-1">
                {(autos.data?.automations ?? [])
                  .filter((a) => a.status !== "archived")
                  .map((a) => (
                    <label key={a.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={(s.start_automation_ids ?? []).includes(a.id)}
                        onChange={(e) => set({ start_automation_ids: e.target.checked ? [...(s.start_automation_ids ?? []), a.id] : (s.start_automation_ids ?? []).filter((x) => x !== a.id) })}
                      />
                      {a.name}
                      <span className="text-xs text-[var(--color-muted-foreground)]">{a.status}</span>
                    </label>
                  ))}
                {(autos.data?.automations ?? []).length === 0 && <span className="text-xs text-[var(--color-muted-foreground)]">No automations yet.</span>}
              </div>
            </Row>
            <Row label="Email the team at" help="The sites already email Brandon for contact forms, so this is usually empty.">
              <Input
                className="h-8"
                aria-label="Email the team at"
                value={(s.notify ?? []).join(", ")}
                onChange={(e) => set({ notify: e.target.value.split(/[\s,]+/).filter(Boolean) })}
                placeholder="team@creuniversity.com"
              />
            </Row>
            {(s.notify ?? []).length > 0 && (
              <Row label="Alert subject">
                <Input className="h-8" aria-label="Alert subject" value={s.notify_subject ?? ""} onChange={(e) => set({ notify_subject: e.target.value || undefined })} />
              </Row>
            )}
          </fieldset>

          <fieldset className="space-y-2 rounded-md border p-3">
            <legend className="px-1 text-xs font-semibold">What goes where on the contact</legend>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              Names, email and phone are always used. Other posted fields fill a contact field only when it's empty, unless "replace" is ticked.
            </p>
            {mapRows.map((r, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <Input className="h-8 min-w-[9rem] flex-1" aria-label={`Posted field ${i + 1}`} value={r.from} placeholder="posted field, e.g. company_name" onChange={(e) => setMapRows(mapRows.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))} />
                <span className="text-xs text-[var(--color-muted-foreground)]">goes to</span>
                <Select className="h-8 min-w-[11rem] flex-[1.3]" aria-label={`Contact field ${i + 1}`} value={r.to} onChange={(e) => setMapRows(mapRows.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))}>
                  <option value="">Choose a field</option>
                  {targets.map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                  {r.to && !targets.some(([k]) => k === r.to) && <option value={r.to}>{r.to}</option>}
                </Select>
                <label className="flex items-center gap-1 text-xs" title="Replace what the contact already has, every time (as Keap did for some fields)">
                  <Checkbox checked={r.overwrite} onChange={(e) => setMapRows(mapRows.map((x, j) => (j === i ? { ...x, overwrite: e.target.checked } : x)))} /> replace
                </label>
                <button aria-label={`Remove mapping ${i + 1}`} className="rounded p-1 hover:bg-[var(--color-muted)]" onClick={() => setMapRows(mapRows.filter((_, j) => j !== i))}>
                  <X className="size-4" />
                </button>
              </div>
            ))}
            {mapProblem && <p className="text-xs text-[var(--color-danger-fg)]">Each posted field needs a contact field, and can be listed only once.</p>}
            <button className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline" onClick={() => setMapRows([...mapRows, { from: "", to: "", overwrite: false }])}>
              <Plus className="size-3" /> Add a field
            </button>
          </fieldset>

          <fieldset className="space-y-2 rounded-md border p-3">
            <legend className="px-1 text-xs font-semibold">Tags by answer</legend>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              When a posted field has a given answer, add these tags (before the other tags, so automations can see them). The answer must match exactly.
            </p>
            {answerRows.map((r, i) => (
              <div key={i} className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto] items-center gap-2 rounded-md bg-[var(--color-muted)]/40 p-2">
                <Input className="h-8" aria-label={`Answer field ${i + 1}`} value={r.field} placeholder="field, e.g. nl_source" onChange={(e) => setAnswerRows(answerRows.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)))} />
                <span className="text-xs text-[var(--color-muted-foreground)]">is</span>
                <Input className="h-8" aria-label={`Answer ${i + 1}`} value={r.value} placeholder="answer, e.g. ads" onChange={(e) => setAnswerRows(answerRows.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
                <button aria-label={`Remove answer rule ${i + 1}`} className="rounded p-1 hover:bg-[var(--color-muted)]" onClick={() => setAnswerRows(answerRows.filter((_, j) => j !== i))}>
                  <X className="size-4" />
                </button>
                <div className="col-span-4">
                  <TagChips ids={r.ids} onChange={(ids) => setAnswerRows(answerRows.map((x, j) => (j === i ? { ...x, ids } : x)))} />
                </div>
              </div>
            ))}
            {answerProblem && <p className="text-xs text-[var(--color-danger-fg)]">Each rule needs a field, an answer and at least one tag.</p>}
            <button className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline" onClick={() => setAnswerRows([...answerRows, { field: "", value: "", ids: [] }])}>
              <Plus className="size-3" /> Add a rule
            </button>
          </fieldset>

          <fieldset className="space-y-2 rounded-md border p-3">
            <legend className="px-1 text-xs font-semibold">Event sign-up</legend>
            <Row label="Register them for" help="For webinar and Boot Camp sign-up forms. Leave it at None for other forms.">
              <Select className="h-8" aria-label="Register them for" value={eventId} onChange={(e) => (setEventId(e.target.value ? Number(e.target.value) : ""), setSessionId(""))}>
                <option value="">None</option>
                {(events.data?.events ?? []).map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name}
                    {ev.status !== "scheduled" ? ` (${ev.status})` : ""}
                  </option>
                ))}
                {eventId && !(events.data?.events ?? []).some((ev) => ev.id === eventId) && <option value={eventId}>Event #{eventId}</option>}
              </Select>
            </Row>
            {eventId !== "" && (
              <Row label="Which date">
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="fs-session" className="accent-[var(--color-primary)]" checked={sessionMode === "next"} onChange={() => setSessionMode("next")} /> The next upcoming date
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="fs-session" className="accent-[var(--color-primary)]" checked={sessionMode === "fixed"} onChange={() => setSessionMode("fixed")} /> Always this date
                  </label>
                  {sessionMode === "fixed" && (
                    <Select className="ml-6 h-8 w-auto" aria-label="Date" value={sessionId} onChange={(e) => setSessionId(e.target.value ? Number(e.target.value) : "")}>
                      <option value="">Choose a date</option>
                      {(eventDetail.data?.sessions ?? []).map((ss) => (
                        <option key={ss.id} value={ss.id}>
                          {ss.name ? `${ss.name}: ` : ""}
                          {formatWhen(ss.starts_at)}
                          {ss.status !== "scheduled" ? ` (${ss.status})` : ""}
                        </option>
                      ))}
                    </Select>
                  )}
                  <label className="flex items-center gap-2">
                    <input type="radio" name="fs-session" className="accent-[var(--color-primary)]" checked={sessionMode === "field"} onChange={() => setSessionMode("field")} /> The date they pick on the form
                  </label>
                  {sessionMode === "field" && (
                    <div className="ml-6 space-y-0.5">
                      <Input className="h-8" aria-label="Field with the date" value={sessionField} onChange={(e) => setSessionField(e.target.value)} placeholder="posted field, e.g. session_id" />
                      <p className="text-[11px] text-[var(--color-muted-foreground)]">The field must hold the date's ID from the event page; anything else falls back to the next upcoming date.</p>
                    </div>
                  )}
                </div>
              </Row>
            )}
          </fieldset>

          {(s.hub_tag_ids ?? []).length > 0 && <HubTags ids={s.hub_tag_ids!} />}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="fs-lead">Lead source for new people</Label>
              <Input id="fs-lead" className="h-8" value={s.lead_source ?? ""} onChange={(e) => set({ lead_source: e.target.value || undefined })} placeholder="Website form" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="fs-spam">Spam check</Label>
              <Select id="fs-spam" value={s.spam || "auto"} onChange={(e) => set({ spam: e.target.value === "auto" ? undefined : (e.target.value as FormSettings["spam"]) })}>
                <option value="auto">Normal (the sites' rules)</option>
                <option value="strict">Strict</option>
                <option value="off">Off</option>
              </Select>
            </div>
          </div>
          {kept.length > 0 && (
            <p className="text-xs text-[var(--color-muted-foreground)]">Also set, and kept as they are (Claude edits these through the MCP): {kept.join(", ").replace(/_/g, " ")}.</p>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending || !name.trim() || mapProblem || answerProblem || (sessionMode === "fixed" && eventId !== "" && !sessionId)} onClick={() => save.mutate()}>
            Save form
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[10rem_minmax(0,1fr)] items-start gap-3">
      <div className="pt-1 text-xs text-[var(--color-muted-foreground)]">
        {label}
        {help && <div className="mt-0.5 text-[11px] leading-snug opacity-80">{help}</div>}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** Tags the site's own code may add through HubService. Shown, not edited here: widening it can grant access. */
function HubTags({ ids }: { ids: number[] }) {
  const tags = useTags(true);
  const name = (id: number) => tags.data?.tags.find((t) => t.id === id)?.name ?? `#${id}`;
  return (
    <div className="space-y-1 rounded-md border border-dashed p-3">
      <div className="text-xs font-semibold">Tags the website's own code may add or remove</div>
      <div className="flex flex-wrap gap-1">
        {ids.map((id) => (
          <Badge key={id} variant="muted">
            {name(id)}
          </Badge>
        ))}
      </div>
      <p className="text-[11px] text-[var(--color-muted-foreground)]">Anything else the site asks for is refused, so it can't grant something like Club access. Claude changes this list, with Brandon's OK.</p>
    </div>
  );
}
